import { makeObservable, observable, action, computed, flow } from "mobx";
import { RenameModalStore } from "../RenameModal";
import { DriveStore } from "../Base/DriveStore";

class DrivesViewStore {
  selectedDrivesSet = new Set();
  filterText = "";
  sortBy = "name";
  sortDirection = "asc";
  isLoading = false;
  error = null;
  viewMode = 'grid';

  isRenameModalOpen = false;
  driveIdBeingRenamed = null;
  driveTitleBeingRenamed = '';
  
  isMoveModalOpen = false;
  driveIdBeingMoved = null;
  
  isMoveModalOpen = false;
  driveIdBeingMoved = null;
  
  constructor({drivesStore}) {
    this.drivesStore = drivesStore;
    this.renameModalStore = new RenameModalStore();
    
    makeObservable(this, {
      selectedDrivesSet: observable,
      filterText: observable,
      sortBy: observable,
      sortDirection: observable,
      isLoading: observable,
      error: observable,
      viewMode: observable,
      isRenameModalOpen: observable,
      driveIdBeingRenamed: observable,
      driveTitleBeingRenamed: observable,
      setSelectedDrives: action,
      toggleDriveSelection: action,
      clearSelection: action,
      setFilterText: action,
      setSortBy: action,
      setViewMode: action,
      createDrive: action,
      openRenameModal: action,
      drives: computed,
      filteredDrives: computed,
      selectedDrives: computed,
      isDriveSelected: computed,
      hasMoreDrives: computed,
      isLoadingMoreDrives: computed,
      loadMoreDrives: flow,
    });
  }

  get drives() {
    return this.drivesStore.drives || [];
  }

  get selectedDrives() {
    return Array.from(this.selectedDrivesSet);
  }

  get isDriveSelected() {
    return (driveId) => this.selectedDrivesSet.has(driveId);
  }

  get hasMoreDrives() {
    return this.drivesStore?.hasMore ?? false;
  }

  get isLoadingMoreDrives() {
    return this.drivesStore?.isLoadingMore ?? false;
  }

  setSelectedDrives = (driveIds) => {
    this.selectedDrivesSet.clear();
    driveIds.forEach(id => this.selectedDrivesSet.add(id));
    this.syncDriveSelectionStates();
  };

  toggleDriveSelection = (driveId) => {
    if (this.selectedDrivesSet.has(driveId)) {
      this.selectedDrivesSet.delete(driveId);
    } else {
      this.selectedDrivesSet.add(driveId);
    }
  };

  clearSelection = () => {
    this.selectedDrivesSet.clear();
    this.drives.forEach(drive => drive.setSelected(false));
  };

  syncDriveSelectionStates = () => {
    this.drives.forEach(drive => {
      const isSelectedInSet = this.selectedDrivesSet.has(drive.id);
      if (drive.isSelected !== isSelectedInSet) {
        drive.setSelected(isSelectedInSet);
      }
    });
  };

  setFilterText = (text) => {
    this.filterText = text;
    // ✅ FIX: Removed fetchDrives call - filtering is done locally via computed filteredDrives
    // The old implementation was calling the server on every filter change, causing unnecessary requests
  };

  setSortBy = (field) => {
    if (this.sortBy === field) {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
    } else {
      this.sortBy = field;
      this.sortDirection = "asc";
    }
  };

  setViewMode = (mode) => {
    this.viewMode = mode;
  };

  openRenameModal = (driveId, currentTitle, onSuccessCallback) => {
    this.renameModalStore.open({
      type: 'drive',
      defaultValue: currentTitle,
      resourceId: driveId,
      onConfirm: async ({ name, resourceId }) => {
        this.drivesStore.renameDrive(resourceId, name || 'Untitled Drive');
        if (onSuccessCallback) {
          onSuccessCallback(resourceId, name);
        }
      }
    });
  };

  closeRenameModal = () => {
    this.isRenameModalOpen = false;
    this.driveIdBeingRenamed = null;
    this.driveTitleBeingRenamed = '';
  };

  setDriveTitleBeingRenamed = (title) => {
    this.driveTitleBeingRenamed = title;
  };

  confirmRename = () => {
    if (!this.driveIdBeingRenamed) return;

    this.drivesStore.renameDrive(
      this.driveIdBeingRenamed,
      this.driveTitleBeingRenamed?.trim() || 'Untitled Drive'
    );

    this.closeRenameModal();
  };

  openMoveModal = (driveId) => {
    this.driveIdBeingMoved = driveId;
    this.isMoveModalOpen = true;
  };

  closeMoveModal = () => {
    this.isMoveModalOpen = false;
    this.driveIdBeingMoved = null;
  };

  loadDrivesData = flow(function* () {
    try {
      this.isLoading = true;
      yield this.drivesStore.fetchDrives("");
    } catch (error) {
      this.error = error;
      console.error('Error loading drives data:', error);
    } finally {
      this.isLoading = false;
    }
  });

  get filteredDrives() {
    let filtered = this.drives;

    if (this.filterText) {
      filtered = filtered.filter(drive => 
        drive.title.toLowerCase().includes(this.filterText.toLowerCase())
      );
    }

    return filtered.sort((a, b) => {
      const direction = this.sortDirection === "asc" ? 1 : -1;
      
      switch (this.sortBy) {
        case "name":
          return direction * a.title.localeCompare(b.title);
        case "lastUpdated":
          return direction * (new Date(a.lastUpdated) - new Date(b.lastUpdated));
        default:
          return 0;
      }
    });
  }

  createDrive = async (driveData) => {
    try {
      const result = await this.drivesStore.createDrive(driveData);
      await this.loadDrivesData();
      return result;
    } catch (error) {
      console.error('Error creating drive:', error);
      throw error;
    }
  };

  refreshDrivesData = async () => {
    await this.loadDrivesData();
  };

  loadMoreDrives = flow(function* () {
    const store = this.drivesStore;

    if (!store || store.isLoadingMore || !store.hasMore) {
      return;
    }

    try {
      const nextPage = (store.currentPage || 1) + 1;
      yield store.fetchDrives(this.filterText || "", nextPage, true);
    } catch (error) {
      console.error("Error loading more drives:", error);
    }
  });
}

export default DrivesViewStore;